import React, { useCallback, useEffect } from 'react';
import { message, notification } from 'antd';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { EVENTS } from 'shared/constants/analytics';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import analyticsService from 'src/helpers/analytics.service';
import { Divider, ScreenTitle } from 'src/components/design-system';
import { AllSettingsButton } from '../../settings.page.component';
import { AnalyzingStep } from './components/analyzing-step';
import { ChooseFileStep } from './components/choose-file-step';
import { ReviewStep } from './components/review-step';
import { SelectTabsStep } from './components/select-tabs-step';
import { UploadingStep } from './components/uploading-step';
import { useShiftsUpload } from './use-shifts-upload.hook';
import { RESULT_VARIANTS, WIZARD_STEPS } from './helpers/upload.types';
import type { ImportResult } from './helpers/upload.types';
import { formatResultTitle, resolveResultVariant } from './helpers/upload.utils';
import styles from './shifts-upload-management.module.scss';

export const ShiftsUploadManagement = () => {
  const lang = useSelector(getLang('SETTINGS'));

  useEffect(() => {
    analyticsService.track(EVENTS.SCHEDULE.UPLOAD_SHIFTS_PAGE_VIEW);
  }, []);

  const announceResult = useCallback(
    (result: ImportResult) => {
      const variant = resolveResultVariant(result);
      const message = formatResultTitle(result, variant, lang);

      if (variant === RESULT_VARIANTS.SUCCESS) {
        notification.success({ message });
        return;
      }

      notification.warning({
        message,
        description:
          result.tabsSkipped > 0
            ? lang.SHIFTS_TABS_EXCLUDED_TOAST.replace('{count}', formatNumber(result.tabsSkipped))
            : lang.SHIFTS_REVIEW_ISSUES_TOAST,
      });
    },
    [lang],
  );

  const announceFailure = useCallback(() => message.error(lang.UPLOAD_FAILED), [lang]);

  const {
    step,
    file,
    sheetNames,
    selectedTabs,
    plan,
    removedCount,
    results,
    lastUpload,
    uploadProgress,
    fileError,
    isReadingFile,
    chooseFile,
    confirmUpload,
    dismissResult,
    toggleTab,
    selectAllTabs,
    clearTabs,
    backToChooseFile,
    backToSelectTabs,
    requestAnalysis,
  } = useShiftsUpload({ onUploaded: announceResult, onUploadFailed: announceFailure });

  return (
    <div className={classNames('new-design-system', styles.root)}>
      <AllSettingsButton />
      <ScreenTitle
        containerStyle={styles.title}
        title={lang.SHIFTS_UPLOAD}
        subtitle={lang.SHIFTS_UPLOAD_SUBTITLE}
      />
      <Divider />
      <div className={styles.content}>
        {step === WIZARD_STEPS.CHOOSE_FILE && (
          <ChooseFileStep
            fileError={fileError}
            isReadingFile={isReadingFile}
            lastUpload={lastUpload}
            results={results}
            onChooseFile={chooseFile}
            onDismissResult={dismissResult}
          />
        )}

        {step === WIZARD_STEPS.SELECT_TABS && file && (
          <SelectTabsStep
            fileName={file.name}
            fileSize={file.size}
            isReadingFile={isReadingFile}
            sheetNames={sheetNames}
            selectedTabs={selectedTabs}
            onChangeFile={chooseFile}
            onToggleTab={toggleTab}
            onSelectAll={selectAllTabs}
            onClear={clearTabs}
            onBack={backToChooseFile}
            onReview={requestAnalysis}
          />
        )}

        {step === WIZARD_STEPS.ANALYZING && <AnalyzingStep />}

        {step === WIZARD_STEPS.REVIEW && file && plan && (
          <ReviewStep
            fileName={file.name}
            fileSize={file.size}
            plan={plan}
            removedCount={removedCount}
            onBack={backToSelectTabs}
            onUpload={confirmUpload}
          />
        )}

        {step === WIZARD_STEPS.UPLOADING && file && (
          <UploadingStep fileName={file.name} fileSize={file.size} progress={uploadProgress} />
        )}
      </div>
    </div>
  );
};
